import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { formatMenuInstant } from '../lib/date'
import { useToast } from '../ui/ToastProvider'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import MenuGuideButton from '../components/MenuGuideButton'
import { Button } from '../components/ui/button'
import { EmptyState } from '../components/ui/feedback-state'
import { RefreshCw } from 'lucide-react'

export default function ClaimApplicationsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { show } = useToast()
  const { hasAnyRole } = useAuth() || { hasAnyRole: () => false }
  const isOperatorUser = hasAnyRole && hasAnyRole([Roles.OPERATOR])
  const [searchParams, setSearchParams] = useSearchParams()
  const [reclaimingId, setReclaimingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  // 현재 경로·쿼리에 따라 활성 탭
  const activeMenu = useMemo(() => {
    const sp = new URLSearchParams(location.search)
    return sp.get('status') === 'DRAFT' ? 'DRAFT_HISTORY' : 'HISTORY'
  }, [location.search])
  
  const [applications, setApplications] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [filters, setFilters] = useState({
    insurerOrgId: null,
    status: searchParams.get('status') || null,
    customerName: '',
    creatorOrgId: null,
    createdByUserId: null,
  })
  const [insurerOrgs, setInsurerOrgs] = useState([])
  const [creatorOrgOptions, setCreatorOrgOptions] = useState([])
  const [creatorUserOptions, setCreatorUserOptions] = useState([])
  /** 운영자 전용: 서버 공통 입력 초안(flow-draft) 존재 여부 — 임시저장 이력 안내용 */
  const [operatorFlowDraftHint, setOperatorFlowDraftHint] = useState(null)

  const loadInsurerOrgs = useCallback(async () => {
    try {
      const res = await api.get('/orgs', { params: { type: 'INSURER', parentId: null } })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const items = Array.isArray(payload?.items) ? payload.items : (Array.isArray(payload) ? payload : [])
      const byId = new Map(items.map((o) => [o.id, o]))
      const companyLevel = items.filter((o) => {
        if (!o || o.type !== 'INSURER' || o.parentOrgId == null) return false
        const parent = byId.get(o.parentOrgId)
        return !!(parent && parent.type === 'INSURER' && parent.parentOrgId == null)
      })
      const insurers = companyLevel.length > 0
        ? companyLevel
        : items.filter((o) => o && o.type === 'INSURER')
      setInsurerOrgs(insurers)
    } catch {
      setInsurerOrgs([])
    }
  }, [])

  useEffect(() => {
    loadInsurerOrgs()
  }, [loadInsurerOrgs])

  useEffect(() => {
    if (!isOperatorUser) {
      setOperatorFlowDraftHint(null)
      return
    }
    let ignore = false
    ;(async () => {
      try {
        const res = await api.get('/claims/flow-draft')
        const wrap = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (ignore) return
        if (wrap?.exists && wrap.updatedAt) {
          setOperatorFlowDraftHint({
            updatedAt: wrap.updatedAt,
            retentionDays: wrap.retentionDays ?? 7,
          })
        } else {
          setOperatorFlowDraftHint(null)
        }
      } catch {
        if (!ignore) setOperatorFlowDraftHint(null)
      }
    })()
    return () => { ignore = true }
  }, [isOperatorUser])

  useEffect(() => {
    let ignore = false
    ;(async () => {
      try {
        const scope = isOperatorUser ? 'all' : 'mine'
        const res = await api.get('/orgs/options', { params: { scope } })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const rows = Array.isArray(payload) ? payload : []
        if (!ignore) setCreatorOrgOptions(rows)
      } catch {
        if (!ignore) setCreatorOrgOptions([])
      }
    })()
    return () => { ignore = true }
  }, [isOperatorUser])

  useEffect(() => {
    if (!filters.creatorOrgId) {
      setCreatorUserOptions([])
      return
    }
    let ignore = false
    ;(async () => {
      try {
        const res = await api.get('/users', {
          params: { orgId: filters.creatorOrgId, page: 0, size: 500, sort: 'name,asc' },
        })
        const page = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const content = Array.isArray(page?.content) ? page.content : []
        if (!ignore) setCreatorUserOptions(content)
      } catch {
        if (!ignore) setCreatorUserOptions([])
      }
    })()
    return () => { ignore = true }
  }, [filters.creatorOrgId])

  /** URL이 진실 소스 — 탭·뒤로가기와 동기화. filters.status만 여기서 반영한다. */
  useEffect(() => {
    const nextStatus = searchParams.get('status') || null
    setFilters((prev) => (prev.status === nextStatus ? prev : { ...prev, status: nextStatus }))
    setPage(0)
  }, [searchParams])

  const listFilterKey = useMemo(
    () => JSON.stringify({
      insurerOrgId: filters.insurerOrgId,
      status: filters.status,
      customerName: filters.customerName,
      creatorOrgId: filters.creatorOrgId,
      createdByUserId: filters.createdByUserId,
    }),
    [
      filters.insurerOrgId,
      filters.status,
      filters.customerName,
      filters.creatorOrgId,
      filters.createdByUserId,
    ],
  )
  const prevListFilterKey = useRef(listFilterKey)

  useEffect(() => {
    const filtersChanged = prevListFilterKey.current !== listFilterKey
    prevListFilterKey.current = listFilterKey
    if (filtersChanged && page !== 0) {
      setPage(0)
      return
    }
    loadApplications()
  }, [page, listFilterKey])

  const loadApplications = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = {
        page,
        size: 20,
      }
      if (filters.insurerOrgId) params.insurerOrgId = filters.insurerOrgId
      if (filters.status) params.status = filters.status
      if (filters.customerName) params.customerName = filters.customerName
      if (filters.creatorOrgId) params.creatorOrgId = filters.creatorOrgId
      if (filters.createdByUserId) params.createdByUserId = filters.createdByUserId

      const res = await api.get('/claims/applications/list', { params })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setApplications(Array.isArray(payload?.items) ? payload.items : [])
      setTotalPages(payload?.totalPages || 0)
    } catch (e) {
      setError('청구 목록을 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const statusLabels = {
    DRAFT: '임시저장',
    SUBMITTED: '접수됨',
    UNDER_REVIEW: '심사중',
    APPROVED: '승인됨',
    REJECTED: '반려됨',
    COMPLETED: '완료됨',
  }

  const statusColors = {
    DRAFT: 'bg-slate-100 text-slate-700',
    SUBMITTED: 'bg-blue-100 text-blue-800',
    UNDER_REVIEW: 'bg-yellow-100 text-yellow-800',
    APPROVED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
    COMPLETED: 'bg-slate-100 text-slate-800',
  }

  const faxReservationColors = {
    SCHEDULED: 'bg-amber-100 text-amber-800',
    SENDING: 'bg-sky-100 text-sky-800',
    SENT: 'bg-emerald-100 text-emerald-700',
    CANCELLED: 'bg-slate-100 text-slate-700',
    FAILED: 'bg-rose-100 text-rose-700',
  }

  const faxQuotaColors = {
    PENDING: 'bg-amber-100 text-amber-800',
    CONSUMED: 'bg-emerald-100 text-emerald-700',
    RELEASED: 'bg-slate-100 text-slate-700',
    SETTLEMENT_FAILED: 'bg-rose-100 text-rose-700',
    NONE: 'bg-slate-100 text-slate-700',
  }

  const claimProgressUi = (app) => {
    const status = app?.status
    const r = app?.faxReservationStatus
    const q = app?.faxQuotaSettlementStatus
    if (status === 'DRAFT') return { label: '임시저장', detail: '작성 중', color: statusColors.DRAFT }
    if (r === 'SCHEDULED') return { label: '팩스 발송 대기', detail: app?.faxReservedAt ? formatMenuInstant(app, 'faxReservedAt') : '보험사 발송 예약됨', color: faxReservationColors.SCHEDULED }
    if (r === 'SENDING') return { label: '팩스 발송 처리 중', detail: '보험사 전송 준비 중', color: faxReservationColors.SENDING }
    if (q === 'CONSUMED') return { label: '팩스 발송 성공', detail: '보험사 수신 확인', color: faxQuotaColors.CONSUMED }
    if (r === 'FAILED') return { label: '팩스 발송 실패', detail: app?.faxLastError || '수신번호와 서류 확인 필요', color: faxReservationColors.FAILED }
    if (r === 'CANCELLED') return { label: '팩스 발송 취소', detail: '전송 전 취소됨', color: faxReservationColors.CANCELLED }
    if (q === 'SETTLEMENT_FAILED') return { label: '결과 확인 필요', detail: app?.faxQuotaLastError || '전송 결과 처리 확인 필요', color: faxQuotaColors.SETTLEMENT_FAILED }
    if (r === 'SENT' || q === 'PENDING' || app?.faxTransmissionId) return { label: '전송 결과 확인 중', detail: '보험사 수신 확인 대기', color: faxQuotaColors.PENDING }
    if (q === 'RELEASED') return { label: '전송 종료', detail: '이용 횟수 반환됨', color: faxQuotaColors.RELEASED }
    if (status === 'COMPLETED') return { label: '청구 완료', detail: '처리 완료', color: statusColors.COMPLETED }
    if (status === 'REJECTED') return { label: '청구 반려', detail: '보완 필요', color: statusColors.REJECTED }
    return { label: '청구 접수', detail: statusLabels[status] || '팩스 발송 전', color: statusColors[status] || 'bg-blue-100 text-blue-800' }
  }

  const guideMeta = activeMenu === 'DRAFT_HISTORY'
    ? { slug: 'claims', title: '보험금청구 가이드', anchor: 'claims-drafts' }
    : { slug: 'claims', title: '보험금청구 가이드', anchor: 'claims-history' }

  const handleReclaim = async (applicationId) => {
    if (!applicationId) return
    setReclaimingId(applicationId)
    try {
      const res = await api.post(`/claims/applications/${applicationId}/reclaim`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const newId = payload?.id
      if (!newId) {
        show('재청구 건을 만들지 못했습니다.', 'error')
        return
      }
      show('새 임시저장 청구가 만들어졌습니다. 청구일·첨부·서명을 확인해 주세요.', 'success')
      navigate(`/support/claims?resumeApplicationId=${newId}`)
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error?.message || ''
      show(msg || '재청구에 실패했습니다.', 'error')
    } finally {
      setReclaimingId(null)
    }
  }

  const handleDeleteApplication = async (app) => {
    if (!app?.id) return
    const label = [app.customerName, app.insurerOrgName].filter(Boolean).join(' / ') || `청구서 #${app.id}`
    const ok = window.confirm(`${label} 청구 신청서를 삭제할까요?\n삭제 후에는 목록에서 제거됩니다.`)
    if (!ok) return
    setDeletingId(app.id)
    try {
      await api.delete(`/claims/applications/${app.id}`)
      show('청구 신청서를 삭제했습니다.', 'success')
      if (applications.length === 1 && page > 0) {
        setPage((prev) => Math.max(prev - 1, 0))
      } else {
        await loadApplications()
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error?.message || ''
      show(msg || '청구 신청서 삭제에 실패했습니다.', 'error')
    } finally {
      setDeletingId(null)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const params = {}
      if (filters.insurerOrgId) params.insurerOrgId = filters.insurerOrgId
      if (filters.status) params.status = filters.status
      if (filters.customerName) params.customerName = filters.customerName
      if (filters.creatorOrgId) params.creatorOrgId = filters.creatorOrgId
      if (filters.createdByUserId) params.createdByUserId = filters.createdByUserId

      const res = await api.get('/claims/applications/export', {
        params,
        responseType: 'blob',
      })

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      const cd = res?.headers?.['content-disposition'] || res?.headers?.['Content-Disposition'] || ''
      const filenameStar = cd && typeof cd === 'string' ? cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i) : null
      const filenamePlain = cd && typeof cd === 'string' ? cd.match(/filename="([^"]+)"/) : null
      const headerFilename = filenameStar
        ? decodeURIComponent(filenameStar[1])
        : filenamePlain
          ? filenamePlain[1]
          : ''

      const filename = headerFilename && headerFilename !== 'claim_applications.xlsx' 
        ? headerFilename 
        : `보험금청구이력_${new Date().toISOString().split('T')[0]}.xlsx`

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      setError('엑셀 내보내기에 실패했습니다.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
          <h1 className="text-2xl font-bold shrink-0">보험금청구</h1>
          <div className="flex max-w-full items-center gap-1 overflow-x-auto rounded-lg border border-sgez-border bg-sgez-surface p-0.5 text-sm">
            <button
              type="button"
              className="shrink-0 whitespace-nowrap rounded bg-sgez-surface px-3 py-1.5 hover:bg-sgez-surface-sunken"
              onClick={() => navigate('/support/claims')}
            >
              보험금 청구
            </button>
            <button
              type="button"
              className={`shrink-0 whitespace-nowrap rounded px-3 py-1.5 ${activeMenu === 'HISTORY' ? 'bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'bg-sgez-surface hover:bg-sgez-surface-sunken'}`}
              onClick={() => navigate('/support/claims/applications')}
            >
              청구 이력
            </button>
            <button
              type="button"
              className={`shrink-0 whitespace-nowrap rounded px-3 py-1.5 ${activeMenu === 'DRAFT_HISTORY' ? 'bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'bg-sgez-surface hover:bg-sgez-surface-sunken'}`}
              onClick={() => navigate('/support/claims/applications?status=DRAFT')}
            >
              임시저장 이력
            </button>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
            onClick={loadApplications}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            새로고침
          </button>
          <MenuGuideButton slug={guideMeta.slug} title={guideMeta.title} anchor={guideMeta.anchor} />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">청구 신청서 목록</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
            onClick={handleExport}
            disabled={loading || exporting}
          >
            {exporting ? '내보내는 중...' : '엑셀 내보내기'}
          </button>
          <button
            type="button"
            className="rounded bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800"
            onClick={() => navigate('/support/claims')}
          >
            신규 청구
          </button>
        </div>
      </div>

      <div className="rounded border bg-white p-3">
        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
          <div className="min-w-[7rem] flex-1 basis-[8rem]">
            <div className="text-xs text-slate-600 mb-1">고객명</div>
            <input
              className="w-full min-w-0 rounded border px-2 py-1.5 text-sm"
              value={filters.customerName}
              onChange={(e) => setFilters({ ...filters, customerName: e.target.value })}
              placeholder="검색"
            />
          </div>
          <div className="min-w-[8rem] flex-1 basis-[9rem]">
            <div className="text-xs text-slate-600 mb-1">보험사</div>
            <select
              className="w-full min-w-0 rounded border px-2 py-1.5 text-sm"
              value={filters.insurerOrgId || ''}
              onChange={(e) => {
                const v = e.target.value
                setFilters({ ...filters, insurerOrgId: v ? Number(v) : null })
              }}
            >
              <option value="">전체</option>
              {insurerOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div className="min-w-[7rem] flex-1 basis-[8rem]">
            <div className="text-xs text-slate-600 mb-1">상태</div>
            <select
              className="w-full min-w-0 rounded border px-2 py-1.5 text-sm"
              value={filters.status || ''}
              onChange={(e) => {
                const v = e.target.value || null
                setFilters((prev) => ({ ...prev, status: v }))
                const next = new URLSearchParams(searchParams)
                if (v) next.set('status', v)
                else next.delete('status')
                setSearchParams(next, { replace: true })
              }}
            >
              <option value="">전체</option>
              <option value="DRAFT">임시저장</option>
              <option value="SUBMITTED">접수됨</option>
              <option value="UNDER_REVIEW">심사중</option>
              <option value="APPROVED">승인됨</option>
              <option value="REJECTED">반려됨</option>
              <option value="COMPLETED">완료됨</option>
            </select>
          </div>
          <div className="min-w-[9rem] flex-1 basis-[10rem]">
            <div className="text-xs text-slate-600 mb-1">작성 소속</div>
            <select
              className="w-full min-w-0 rounded border px-2 py-1.5 text-sm"
              value={filters.creatorOrgId || ''}
              onChange={(e) => {
                const v = e.target.value
                setFilters({
                  ...filters,
                  creatorOrgId: v ? Number(v) : null,
                  createdByUserId: null,
                })
              }}
            >
              <option value="">전체</option>
              {creatorOrgOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.name || o.label || o.id}</option>
              ))}
            </select>
          </div>
          <div className="min-w-[9rem] flex-1 basis-[10rem]">
            <div className="text-xs text-slate-600 mb-1">작성자</div>
            <select
              className="w-full min-w-0 rounded border px-2 py-1.5 text-sm"
              value={filters.createdByUserId || ''}
              onChange={(e) => {
                const v = e.target.value
                setFilters({ ...filters, createdByUserId: v ? Number(v) : null })
              }}
              disabled={!filters.creatorOrgId}
            >
              <option value="">{filters.creatorOrgId ? '전체(소속)' : '조직 선택'}</option>
              {creatorUserOptions.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {filters.status === 'DRAFT' && (
        <div className="rounded border border-sky-100 bg-sky-50/90 p-3 text-sm text-slate-800 space-y-2">
          <p>
            이 목록은 <strong className="font-semibold text-slate-900">보험사별 청구서</strong> 건 중 상태가 「임시저장」인 것만 보여 줍니다.
            보험금 청구 화면의 <strong className="font-semibold text-slate-900">공통 입력 임시저장</strong>은 청구서와 별개로
            {isOperatorUser ? ' 브라우저와 운영자 서버 ' : ' 이 브라우저 '}
            에만 저장되며, 아래 표에 자동으로 나타나지 않습니다.
          </p>
          {isOperatorUser && operatorFlowDraftHint && (
            <div className="flex flex-col gap-2 rounded border border-sky-200 bg-white px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm">
                <span className="font-semibold text-slate-900">저장된 공통 입력 초안(운영자)</span>
                <span className="ml-2 text-slate-600">
                  마지막 저장 {formatMenuInstant(operatorFlowDraftHint, 'updatedAt')} · 약 {operatorFlowDraftHint.retentionDays}일 보관
                </span>
              </div>
              <Button type="button" variant="sgezOutline" size="sm" className="shrink-0" onClick={() => navigate('/support/claims')}>
                보험금 청구에서 이어쓰기
              </Button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {loading && <div className="text-center py-8 text-slate-600">불러오는 중...</div>}

      {!loading && applications.length === 0 && (
        <EmptyState
          title="등록된 청구가 없습니다"
          actionLabel="신규 청구 시작"
          onAction={() => navigate('/support/claims')}
          className="rounded border border-dashed bg-white"
        />
      )}

      {!loading && applications.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-sgez-border-strong bg-sgez-surface">
          <table className="sgez-sticky-edge-table w-full min-w-[760px] text-sm">
            <thead className="border-b border-sgez-border-strong bg-sgez-surface-sunken">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">생성일</th>
                <th className="px-4 py-3 text-left font-semibold">고객명</th>
                <th className="px-4 py-3 text-left font-semibold">보험사</th>
                <th className="px-4 py-3 text-left font-semibold">작성</th>
                <th className="px-4 py-3 text-left font-semibold">진행 상태</th>
                <th className="px-4 py-3 text-left font-semibold">작업</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.id} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatMenuInstant(app, 'createdAt')}
                  </td>
                  <td className="px-4 py-3">{app.customerName || '-'}</td>
                  <td className="px-4 py-3">{app.insurerOrgName || '-'}</td>
                  <td className="px-4 py-3 text-xs text-slate-700">
                    <div className="font-medium text-slate-900">{app.createdByUserName || '-'}</div>
                    <div className="text-slate-500">{app.createdByOrgName || ''}</div>
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const progress = claimProgressUi(app)
                      return (
                        <div className="space-y-1">
                          <span className={`inline-flex rounded px-2 py-1 text-xs font-medium ${progress.color}`}>
                            {progress.label}
                          </span>
                          {progress.detail && (
                            <div className="max-w-[220px] truncate text-xs text-slate-500" title={progress.detail}>
                              {progress.detail}
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800"
                        onClick={() => navigate(`/support/claims/${app.id}`)}
                      >
                        상세
                      </button>
                      {app.status === 'DRAFT' && (
                        <button
                          type="button"
                          className="text-slate-700 hover:text-slate-900"
                          onClick={() => navigate(`/support/claims?resumeApplicationId=${app.id}`)}
                        >
                          이어쓰기
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
                        disabled={reclaimingId === app.id}
                        title="고객·보험사·내용·첨부는 복사하고, 청구일·팩스·서명은 새로 진행합니다."
                        onClick={() => handleReclaim(app.id)}
                      >
                        {reclaimingId === app.id ? '재청구…' : '재청구'}
                      </button>
                      <button
                        type="button"
                        className="text-rose-700 hover:text-rose-900 disabled:opacity-50"
                        disabled={deletingId === app.id}
                        onClick={() => handleDeleteApplication(app)}
                      >
                        {deletingId === app.id ? '삭제 중…' : '삭제'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t">
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm disabled:opacity-50"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                이전
              </button>
              <span className="text-sm text-slate-600">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm disabled:opacity-50"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
              >
                다음
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
